import { useState, useCallback, useRef } from 'react'
import type { ConnectMessage, OptimisticMessage } from '../types'
import { mergeFetchedMessages } from '../utils/mergeFetchedMessages'
import { shouldRestFallback } from '../utils/socketSend'

const PAGE_SIZE = 50

export interface UseMessagesOptions {
  apiBase: string
  getAuthToken: () => string | Promise<string>
  currentUserId?: number
}

export function useMessages(options: UseMessagesOptions) {
  const { apiBase, getAuthToken, currentUserId } = options

  const [messages, setMessages] = useState<(ConnectMessage | OptimisticMessage)[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const abortRef = useRef<AbortController | null>(null)

  const authHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }, [getAuthToken])

  const fetchMessages = useCallback(async (conversationId: string, before?: string) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoading(true)

    try {
      const headers = await authHeaders()
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) })
      if (before) params.set('before', before)

      const res = await fetch(
        `${apiBase}/conversations/${conversationId}/messages/?${params}`,
        { headers, signal: controller.signal },
      )
      if (!res.ok) {
        if (res.status === 404) {
          // Conversation deleted or doesn't exist — clear messages silently
          setMessages([])
          setHasMore(false)
          return
        }
        throw new Error(`Failed to fetch messages: ${res.status}`)
      }

      const data = await res.json()
      const fetched: ConnectMessage[] = Array.isArray(data) ? data : data.messages ?? data.results ?? []
      setHasMore(fetched.length >= PAGE_SIZE)

      if (before) {
        setMessages((prev) => [...fetched, ...prev])
      } else {
        setMessages((prev) => mergeFetchedMessages(fetched, prev))
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        throw err
      }
    } finally {
      setIsLoading(false)
    }
  }, [apiBase, authHeaders])

  const sendMessage = useCallback(async (
    conversationId: string,
    content: string,
    wsSend: (conversationId: string, content: string, clientMessageId: string) => boolean | void,
    wsConnected?: boolean,
  ) => {
    // Generate UUID v4 — crypto.randomUUID() requires secure context (HTTPS)
    let clientMessageId: string
    if (typeof crypto.randomUUID === 'function') {
      clientMessageId = crypto.randomUUID()
    } else {
      // Fallback UUID v4 generator for HTTP contexts
      clientMessageId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
      })
    }

    const optimistic: OptimisticMessage = {
      id: clientMessageId,
      conversation_id: conversationId,
      sender_id: currentUserId ?? 0,
      sender_email: '',
      sender_name: '',
      sender_username: '',
      sender_is_agent: false,
      agent_id: null,
      message_type: 'text',
      content,
      media_url: '',
      media_metadata: {},
      reply_to: null,
      reply_to_content: null,
      reply_to_sender_name: null,
      reply_to_message_type: null,
      reply_to_media_url: null,
      forwarded_from: null,
      forwarded_from_content: null,
      forwarded_from_sender_name: null,
      reactions: {},
      is_edited: false,
      is_deleted: false,
      is_starred: false,
      read_by: [],
      delivered_to: [],
      client_message_id: clientMessageId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _optimistic: true,
      _sending: true,
    }

    setMessages((prev) => [...prev, optimistic])

    const accepted = Boolean(wsConnected) && wsSend(conversationId, content, clientMessageId) !== false
    if (shouldRestFallback(Boolean(wsConnected), accepted)) {
      // Socket down, or isConnected was stale while the socket was not OPEN.
      try {
        const headers = await authHeaders()
        const res = await fetch(`${apiBase}/conversations/${conversationId}/messages/`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ content, message_type: 'text', client_message_id: clientMessageId }),
        })
        if (res.ok) {
          const serverMsg = await res.json()
          setMessages((prev) =>
            prev.map(m => m.client_message_id === clientMessageId ? { ...serverMsg, _optimistic: false, _sending: false } : m)
          )
        } else {
          setMessages((prev) =>
            prev.map(m => m.client_message_id === clientMessageId ? { ...m, _sending: false, _failed: true } as OptimisticMessage : m)
          )
        }
      } catch {
        setMessages((prev) =>
          prev.map(m => m.client_message_id === clientMessageId ? { ...m, _sending: false, _failed: true } as OptimisticMessage : m)
        )
      }
    }

    return clientMessageId
  }, [currentUserId, apiBase, authHeaders])

  const addMessage = useCallback((msg: ConnectMessage) => {
    setMessages((prev) => {
      if (msg.client_message_id) {
        const idx = prev.findIndex(
          (m) => (m as OptimisticMessage)._optimistic && m.client_message_id === msg.client_message_id,
        )
        if (idx !== -1) {
          const next = [...prev]
          next[idx] = msg
          return next
        }
      }
      if (prev.some((m) => m.id === msg.id)) return prev
      return [...prev, msg]
    })
  }, [])

  const updateMessage = useCallback((partial: Partial<ConnectMessage> & { id: string }) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === partial.id ? { ...m, ...partial } : m)),
    )
  }, [])

  const markDeleted = useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId ? { ...m, is_deleted: true, content: '' } : m,
      ),
    )
  }, [])

  const markFailed = useCallback((clientMessageId: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.client_message_id === clientMessageId
          ? { ...m, _sending: false, _failed: true } as OptimisticMessage
          : m,
      ),
    )
  }, [])

  const loadMore = useCallback(async (conversationId: string) => {
    if (!hasMore || isLoading) return
    const oldest = messages[0]
    if (oldest) {
      await fetchMessages(conversationId, oldest.id)
    }
  }, [hasMore, isLoading, messages, fetchMessages])

  const clearMessages = useCallback(() => {
    setMessages([])
    setHasMore(true)
  }, [])

  return {
    messages,
    isLoading,
    hasMore,
    fetchMessages,
    sendMessage,
    addMessage,
    updateMessage,
    markDeleted,
    markFailed,
    loadMore,
    clearMessages,
  }
}
